import React, { useEffect, useState, useCallback } from "react";
import useRefetchOnFocus from "@/hooks/useRefetchOnFocus";
import Logo from "@/components/Logo";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, GripVertical, Pencil, Trash2, Check, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Chat from "@/components/Chat";
import MostrarPantalla from "@/components/MostrarPantalla";
import Tour from "@/components/Tour";
import { useMomentos } from "@/context/MomentosContext";

const TOUR_PROCESO = [
  { selector: '[data-testid="proceso-chat"]', texto: "Contame cómo hacés esto hoy, paso por paso. No tiene que salir perfecto: lo vamos ordenando juntos." },
  { selector: '[data-testid="proceso-pasos"]', texto: "Acá se van escribiendo los pasos a medida que me contás. Los podés editar, borrar o reordenar arrastrándolos." },
];

export default function Proceso() {
  const { id } = useParams();
  const nav = useNavigate();
  const [proc, setProc] = useState(null);
  const [pasos, setPasos] = useState([]);
  const [disparador, setDisparador] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState({ descripcion: "", responsable: "", herramienta: "" });
  const [dragIdx, setDragIdx] = useState(null);
  const [readiness, setReadiness] = useState({ listo: false, faltantes: [] });
  const [implEstado, setImplEstado] = useState(null);
  const [cambiosAndando, setCambiosAndando] = useState(false);
  const [regenerando, setRegenerando] = useState(false);
  const [fase, setFase] = useState(null); // "entrevista" | "resultado"
  const [mostrarPantalla, setMostrarPantalla] = useState(null); // oferta "Mostrame tu pantalla"
  const [grabarNonce, setGrabarNonce] = useState(null); // dispara el micrófono del chat
  const { pedir } = useMomentos();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/proceso/${id}`);
      setProc(data.proceso);
      setPasos(data.proceso.pasos || []);
      setDisparador(data.proceso.disparador || "");
      if (data.readiness) setReadiness(data.readiness);
      setImplEstado(data.impl_estado || null);
      // Fase inicial: si ya está listo o ya tiene implementación, mostramos el proceso escrito; si no, la entrevista.
      setFase((data.readiness?.listo || data.impl_estado) ? "resultado" : "entrevista");
      // Momento 3: antes de definir el primer proceso (proceso fresco, sin pasos aún).
      if (!(data.proceso.pasos || []).length && !data.proceso.disparador) pedir("m3");
    })();
  }, [id]);

  const regenerar = () => {
    setRegenerando(true);
    // La regeneración llama al LLM (tarda): la hacemos en /implementar con su pantalla de "generando".
    nav(`/implementar/${id}?regenerar=1`);
  };

  const recargarProc = useCallback(async () => {
    try {
      const { data } = await api.get(`/proceso/${id}`);
      setProc(data.proceso);
      setPasos(data.proceso.pasos || []);
      setDisparador(data.proceso.disparador || "");
      if (data.readiness) setReadiness(data.readiness);
      setImplEstado(data.impl_estado || null);
    } catch (e) { /* noop */ }
  }, [id]);

  useRefetchOnFocus(recargarProc, !!proc);

  const onAcciones = (data) => {
    if (data?.pasos_full) setPasos(data.pasos_full);
    if (data?.disparador) setDisparador(data.disparador);
    if (data?.readiness) setReadiness(data.readiness);
    if (data?.mostrar_pantalla) setMostrarPantalla(data.mostrar_pantalla);
    if ((data?.actualizo || []).includes("proceso")) recargarProc();
    // El asistente decidió que el proceso está listo: pasamos a la pantalla del proceso escrito.
    if (data?.cerrado) { recargarProc(); setFase("resultado"); }
  };

  const persist = async (lista) => {
    const limpio = lista.map((p) => ({
      descripcion: p.descripcion, responsable: p.responsable || "",
      herramienta: p.herramienta || "", frena: !!p.frena, motivo_freno: p.motivo_freno || "",
    }));
    // optimista
    setPasos(limpio.map((p, i) => ({ ...p, orden: i + 1 })));
    try {
      const { data } = await api.patch(`/proceso/${id}/pasos`, { pasos: limpio });
      setPasos(data.proceso.pasos || []);
      if (data.readiness) setReadiness(data.readiness);
      if (implEstado === "andando") setCambiosAndando(true);
    } catch {
      toast.error("No se pudo guardar el cambio.");
    }
  };

  const seguirContando = () => {
    setFase("entrevista");
  };

  // Botón dentro del chat: el asistente invitó a avanzar. Pasamos al proceso escrito
  // (o directo a implementar si ya está listo).
  const onAvanzar = async (tipo) => {
    if (tipo === "implementar_proceso") {
      await confirmar();
      return;
    }
    recargarProc();
    setFase("resultado");
  };

  const ordenados = [...pasos].sort((a, b) => a.orden - b.orden);
  const vivos = ordenados.length;

  const empezarEdit = (idx) => {
    setEditIdx(idx);
    const p = ordenados[idx];
    setDraft({ descripcion: p.descripcion || "", responsable: p.responsable || "", herramienta: p.herramienta || "" });
  };
  const guardarEdit = (idx) => {
    if (!draft.descripcion.trim()) { toast.error("El paso no puede quedar vacío."); return; }
    const lista = [...ordenados];
    lista[idx] = { ...lista[idx], ...draft };
    setEditIdx(null);
    persist(lista);
  };
  const borrar = (idx) => {
    const lista = ordenados.filter((_, i) => i !== idx);
    persist(lista);
  };
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const lista = [...ordenados];
    const [moved] = lista.splice(dragIdx, 1);
    lista.splice(idx, 0, moved);
    setDragIdx(null);
    persist(lista);
  };

  // Inserta un paso nuevo vacío en la posición dada y lo deja en modo edición.
  // No se persiste hasta que la persona lo guarda (el backend descarta pasos sin texto).
  const agregarPaso = (insertIdx) => {
    if (editIdx !== null) return;
    const nuevo = { orden: 0, descripcion: "", responsable: "", herramienta: "", frena: false, motivo_freno: "", _nuevo: true };
    const lista = [...ordenados];
    lista.splice(insertIdx, 0, nuevo);
    setPasos(lista.map((p, i) => ({ ...p, orden: i + 1 })));
    setEditIdx(insertIdx);
    setDraft({ descripcion: "", responsable: "", herramienta: "" });
  };
  const cancelarEdit = (idx) => {
    const p = ordenados[idx];
    setEditIdx(null);
    if (p && p._nuevo) {
      setPasos(ordenados.filter((_, i) => i !== idx).map((x, i) => ({ ...x, orden: i + 1 })));
    }
  };

  const confirmar = async () => {
    try {
      await api.post(`/proceso/${id}/confirmar`);
      nav(`/implementar/${id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.mensaje || "Todavía falta info para implementarlo.") : "Todavía falta info para implementarlo.");
      if (d && d.faltantes) setReadiness({ listo: false, faltantes: d.faltantes });
    }
  };

  return (
    <div className="h-screen bg-negro flex flex-col">
      <header className="h-14 border-b border-borde flex items-center justify-between px-6 shrink-0">
        <Logo variant="mark" height={24} />
        <button onClick={() => nav("/")} className="mono-label text-gris hover:text-crema flex items-center gap-2 transition-colors" data-testid="volver-mesa">
          <ArrowLeft size={14} /> MI MESA
        </button>
      </header>

      <Tour id="proceso" steps={TOUR_PROCESO} />
      {fase === "resultado" ? (
      <div className="flex-1 overflow-y-auto" data-testid="proceso-resultado">
        <div className="max-w-[760px] mx-auto px-8 py-10">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setFase("entrevista")} className="mono-label text-gris hover:text-crema flex items-center gap-2 transition-colors" data-testid="volver-al-chat">
              <ArrowLeft size={14} /> VOLVER AL CHAT
            </button>
            <span className="mono-label text-dorado" data-testid="contador-pasos">{vivos} PASOS</span>
          </div>
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="titulo text-3xl">{proc?.nombre}</h2>
          </div>
          {vivos > 0 && (
            <p className="text-gris text-xs mb-6" data-testid="pasos-ayuda">
              Este es tu proceso escrito. Podés editar el texto, agregar o borrar un paso, arrastrarlo para cambiar el orden, o volver al chat a seguir contando.
            </p>
          )}

          {disparador && (
            <div className="card-dark p-4 mb-6" style={{ borderColor: "rgba(201,168,76,0.3)" }}>
              <div className="mono-label text-dorado mb-1">ARRANCA CUANDO</div>
              <p className="text-crema text-sm">{disparador}</p>
            </div>
          )}

          <div className="space-y-3">
            <AnimatePresence>
              {ordenados.map((p, idx) => {
                const enEdit = editIdx === idx;
                return (
                  <motion.div
                    key={`${p.orden}-${idx}`}
                    layout
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`relative flex gap-3 group rounded-md ${dragIdx === idx ? "opacity-40" : ""}`}
                    data-testid={`paso-${p.orden}`}
                    draggable={!enEdit}
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(idx)}
                    onDragEnd={() => setDragIdx(null)}
                  >
                    {idx >= 1 && editIdx === null && (
                      <div className="absolute -top-3 left-0 right-0 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button
                          onClick={() => agregarPaso(idx)}
                          className="flex items-center justify-center w-5 h-5 rounded-full bg-negro border border-dorado text-dorado hover:bg-dorado hover:text-negro transition-colors"
                          data-testid={`paso-insertar-${p.orden}`}
                          title="Insertar un paso acá"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    )}
                    <div className="flex items-start gap-1 pt-1 shrink-0">
                      <span className="text-gris cursor-grab active:cursor-grabbing" data-testid={`paso-drag-${p.orden}`}>
                        <GripVertical size={14} />
                      </span>
                      <span className="mono-label text-dorado w-6">{String(p.orden).padStart(2, "0")}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      {enEdit ? (
                        <div className="space-y-2">
                          <textarea
                            className="input-dark w-full text-sm p-2 resize-none"
                            rows={2}
                            value={draft.descripcion}
                            onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
                            data-testid={`paso-input-${p.orden}`}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <input className="input-dark flex-1 text-xs p-1.5" placeholder="Quién lo hace"
                              value={draft.responsable} onChange={(e) => setDraft({ ...draft, responsable: e.target.value })} />
                            <input className="input-dark flex-1 text-xs p-1.5" placeholder="Con qué"
                              value={draft.herramienta} onChange={(e) => setDraft({ ...draft, herramienta: e.target.value })} />
                          </div>
                          <div className="flex gap-2">
                            <button className="btn-dorado text-xs px-3 py-1 flex items-center gap-1" onClick={() => guardarEdit(idx)} data-testid={`paso-guardar-${p.orden}`}>
                              <Check size={12} /> GUARDAR
                            </button>
                            <button className="btn-fantasma text-xs px-3 py-1 flex items-center gap-1" onClick={() => cancelarEdit(idx)} data-testid={`paso-cancelar-${p.orden}`}>
                              <X size={12} /> CANCELAR
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-crema">{p.descripcion}</p>
                          <div className="flex gap-3 mt-1 items-center flex-wrap">
                            {p.responsable && <span className="mono-label text-gris" style={{ fontSize: "10px" }}>{p.responsable}</span>}
                            {p.herramienta && <span className="mono-label text-gris" style={{ fontSize: "10px" }}>{p.herramienta}</span>}
                            {p.frena && (
                              <span className="text-rojo" style={{ fontSize: "11px", textTransform: "none", letterSpacing: "0.02em", fontFamily: "'JetBrains Mono', monospace" }} data-testid={`paso-frena-${p.orden}`}>
                                acá se frena{p.motivo_freno ? ` · ${p.motivo_freno.toLowerCase()}` : ""}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {!enEdit && (
                      <div className="flex items-start gap-1 pt-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button className="text-gris hover:text-dorado p-1" onClick={() => empezarEdit(idx)} data-testid={`paso-editar-${p.orden}`} title="Editar">
                          <Pencil size={13} />
                        </button>
                        <button className="text-gris hover:text-rojo p-1" onClick={() => borrar(idx)} data-testid={`paso-borrar-${p.orden}`} title="Borrar">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          <button
            onClick={() => agregarPaso(ordenados.length)}
            disabled={editIdx !== null}
            className="w-full mt-3 py-2 rounded-md border border-dashed border-borde text-gris hover:text-dorado hover:border-dorado transition-colors flex items-center justify-center gap-2 mono-label disabled:opacity-40 disabled:hover:text-gris disabled:hover:border-borde"
            data-testid="agregar-paso"
          >
            <Plus size={14} /> AGREGAR UN PASO
          </button>
          {implEstado === "andando" && (
            <div className="card-dark p-5 mt-10" style={{ borderColor: cambiosAndando ? "rgba(201,168,76,0.5)" : "rgba(111,191,169,0.4)" }} data-testid="andando-editar-bar">
              {cambiosAndando ? (
                <>
                  <p className="text-crema text-sm mb-4">Editaste un proceso que ya está andando. ¿Querés volver a generar la implementación con los cambios?</p>
                  <div className="flex flex-wrap gap-3">
                    <button className="btn-dorado" disabled={regenerando} onClick={regenerar} data-testid="regenerar-impl">{regenerando ? "REGENERANDO…" : "REGENERAR LA IMPLEMENTACIÓN"}</button>
                    <button className="btn-fantasma" disabled={regenerando} onClick={() => setCambiosAndando(false)} data-testid="dejar-asi">DEJAR ASÍ</button>
                  </div>
                </>
              ) : (
                <p className="text-gris text-sm">Este proceso ya está andando. Podés editar los pasos, el responsable o la herramienta acá arriba; al guardar te pregunto si querés regenerar la implementación.</p>
              )}
            </div>
          )}
          {implEstado !== "andando" && (vivos > 0 || disparador) && (
            readiness.listo ? (
              <button className="btn-dorado w-full mt-10" onClick={confirmar} data-testid="confirmar-proceso">IMPLEMENTAR ESTE PROCESO</button>
            ) : (
              <div className="card-dark p-5 mt-10" style={{ borderColor: "rgba(201,168,76,0.3)" }} data-testid="falta-para-implementar">
                <div className="mono-label text-dorado mb-3">NOS FALTA POCO PARA PODER IMPLEMENTARLO</div>
                <ul className="space-y-2 mb-5">
                  {readiness.faltantes.map((f, i) => (
                    <li key={i} className="text-crema text-sm flex gap-2" data-testid={`falta-item-${i}`}>
                      <span className="text-dorado shrink-0">·</span><span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button className="btn-fantasma w-full" onClick={seguirContando} data-testid="seguir-contando">SEGUIR CONTANDO</button>
              </div>
            )
          )}
        </div>
      </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="w-full max-w-[760px] mx-auto flex-1 flex flex-col min-h-0 p-6" data-testid="proceso-chat">
            <Chat contexto="proceso" procesoId={id} onAcciones={onAcciones} onAvanzar={onAvanzar} grabarNonce={grabarNonce} placeholder="Contame cómo lo hacés, o mandame una captura…" />
            {mostrarPantalla && (
              <div className="shrink-0 mt-3">
                <MostrarPantalla
                  so={mostrarPantalla.sistema_operativo}
                  procesoId={id}
                  onHablando={() => { setMostrarPantalla(null); setGrabarNonce(Date.now()); }}
                  onCerrar={() => setMostrarPantalla(null)}
                  onListo={() => { setMostrarPantalla(null); recargarProc(); setFase("resultado"); }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
